/**
 * gradual-message.ts
 * 逐字流式工具：createTextStream 把完整文本合成「假打字机流」喂给 PoppinText 做逐字动画。
 * （历史上这里曾是逐条发送实现，现核心是流式逐字。）
 */

// 延迟函数
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// 生成 [min, max) 区间的随机延迟时间（ms）
export function getRandomDelay(min: number, max: number): number {
  return Math.random() * (max - min) + min
}

/**
 * 逐字流式播放的统一时序常量。
 * 生成（createTextStream）与时长估算（estimateStreamDuration）共用同一套，
 * 避免「生成用一套数、估算用另一套数」导致的清除时机不同步。
 */
export const STREAM_TIMING = {
  /** 每个普通字符的延迟（ms） */
  charDelay: 25,
  /** 标点字符延迟的放大倍数 */
  punctuationFactor: 2.5,
  /** 标点匹配正则（命中则延迟 charDelay * punctuationFactor） */
  punctuationRe: /[，。！？、；：…—\n,.!?;:]/,
} as const

/** createTextStream 返回值 */
export interface TextStreamHandle {
  /** 喂给 PoppinText 的字节流 */
  stream: ReadableStream<Uint8Array>
  /**
   * 流真正播放完毕（controller.close）后 resolve；被下游 cancel 时也会 resolve。
   * 调用方应 await done 后再清除 textStream，而非用墙钟时长估算。
   */
  done: Promise<void>
}

/**
 * 将文本转换为 ReadableStream<Uint8Array>，按字符逐个吐出，用于 PoppinText 逐字动画。
 *
 * @param text - 完整文本
 * @param charDelay - 每个普通字符的延迟（ms），默认 STREAM_TIMING.charDelay
 * @returns { stream, done } —— done 在流真正播放完毕或被 cancel 后 resolve
 */
export function createTextStream(text: string, charDelay = STREAM_TIMING.charDelay): TextStreamHandle {
  const encoder = new TextEncoder()
  const chars = Array.from(text) // 支持 emoji 等多字节字符

  let cancelled = false
  let resolveDone!: () => void
  const done = new Promise<void>((resolve) => {
    resolveDone = resolve
  })

  const stream = new ReadableStream<Uint8Array>({
    async pull(controller) {
      try {
        for (let i = 0; i < chars.length; i++) {
          // 下游取消（如切换会话、组件卸载）后立即停止空转
          if (cancelled)
            return
          controller.enqueue(encoder.encode(chars[i]))
          const isPunctuation = STREAM_TIMING.punctuationRe.test(chars[i])
          await sleep(isPunctuation ? charDelay * STREAM_TIMING.punctuationFactor : charDelay)
        }
        // 正常播放完毕
        controller.close()
      }
      catch {
        // stream 已被下游 cancel，enqueue/close 抛错，静默退出
      }
      finally {
        resolveDone()
      }
    },
    cancel() {
      cancelled = true
      resolveDone()
    },
  })

  return { stream, done }
}

/**
 * 估算逐字流播放总时长（ms），与 createTextStream 共用同一套时序常量。
 * 仅用于无法 await done 的兜底场景；优先使用 createTextStream 返回的 done。
 */
export function estimateStreamDuration(text: string, charDelay = STREAM_TIMING.charDelay): number {
  let total = 0
  for (const ch of text) {
    total += STREAM_TIMING.punctuationRe.test(ch) ? charDelay * STREAM_TIMING.punctuationFactor : charDelay
  }
  return total
}
